import { useState } from "react";
import { Input } from "./ui/input";
import { X, Plus, Tag } from "lucide-react";

const PREDEFINED_TAGS = ["Health", "Family", "Work", "Financial", "Spiritual", "Relationships", "Guidance", "Gratitude", "Other"];

const TAG_COLORS = {
  Health: "bg-red-100 text-red-700 border-red-200",
  Family: "bg-blue-100 text-blue-700 border-blue-200",
  Work: "bg-amber-100 text-amber-700 border-amber-200",
  Financial: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Spiritual: "bg-purple-100 text-purple-700 border-purple-200",
  Relationships: "bg-pink-100 text-pink-700 border-pink-200",
  Guidance: "bg-indigo-100 text-indigo-700 border-indigo-200",
  Gratitude: "bg-teal-100 text-teal-700 border-teal-200",
  Other: "bg-stone-200 text-stone-700 border-stone-300",
};

function getTagColor(tag) {
  return TAG_COLORS[tag] || "bg-sage/10 text-sage border-sage/20";
}

export { PREDEFINED_TAGS, getTagColor };

export function TagSelector({ selectedTags = [], onChange, testIdPrefix = "" }) {
  const [customInput, setCustomInput] = useState("");

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      onChange(selectedTags.filter((t) => t !== tag));
    } else {
      onChange([...selectedTags, tag]);
    }
  };

  const addCustomTag = () => {
    const trimmed = customInput.trim();
    if (trimmed && !selectedTags.includes(trimmed)) {
      onChange([...selectedTags, trimmed]);
    }
    setCustomInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addCustomTag();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 mb-1">
        <Tag className="w-3.5 h-3.5 text-stone-dark/50" />
        <span className="text-sm font-medium text-stone-dark">Tags</span>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}tag-selector`}>
        {PREDEFINED_TAGS.map((tag) => (
          <button
            key={tag}
            type="button"
            onClick={() => toggleTag(tag)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
              selectedTags.includes(tag)
                ? getTagColor(tag) + " ring-1 ring-offset-1 ring-current"
                : "bg-white text-stone-dark/50 border-stone hover:border-stone-dark/30"
            }`}
            data-testid={`${testIdPrefix}tag-${tag.toLowerCase()}`}
          >
            {tag}
          </button>
        ))}
      </div>
      {/* Custom tag input */}
      <div className="flex items-center gap-2 mt-2">
        <Input
          placeholder="Add custom tag..."
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={handleKeyDown}
          className="text-sm h-8 flex-1"
          data-testid={`${testIdPrefix}custom-tag-input`}
        />
        <button
          type="button"
          onClick={addCustomTag}
          disabled={!customInput.trim()}
          className="h-8 w-8 rounded-full bg-sage/10 hover:bg-sage/20 flex items-center justify-center text-sage disabled:opacity-30 transition-colors shrink-0"
          data-testid={`${testIdPrefix}add-custom-tag-btn`}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Show selected custom tags */}
      {selectedTags.filter((t) => !PREDEFINED_TAGS.includes(t)).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {selectedTags.filter((t) => !PREDEFINED_TAGS.includes(t)).map((tag) => (
            <span
              key={tag}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getTagColor(tag)}`}
            >
              {tag}
              <button type="button" onClick={() => toggleTag(tag)} className="hover:opacity-70">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function TagDisplay({ tags = [] }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-2" data-testid="prayer-tags">
      {tags.map((tag) => (
        <span
          key={tag}
          className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${getTagColor(tag)}`}
        >
          {tag}
        </span>
      ))}
    </div>
  );
}
